import { useCallback, useState } from "react";
import Cropper, { type Area } from "react-easy-crop";
import { type Control, type FieldPath, useController } from "react-hook-form";
import type { FieldValues } from "react-hook-form/dist/types/fields";
import { TbPhoto, TbUpload, TbX } from "react-icons/tb";
import { RangeInput } from "@/common/RangeInput";
import { Button } from "@/components/Button";
import { Dropzone } from "@/components/form/Dropzone";
import { FileInputBtn } from "@/components/form/FileInputBtn";
import styles from "./LogoUpload.module.css";

async function getCroppedImg(
  imageSrc: string,
  pixelCrop: Area,
): Promise<Blob | null> {
  const image = new Image();
  image.src = imageSrc;
  await new Promise((resolve) => {
    image.onload = resolve;
  });

  const canvas = document.createElement("canvas");
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return null;
  }

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, "image/png");
  });
}

const imageAccept = {
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/webp": [".webp"],
};

interface LogoUploadProps<T extends FieldValues> {
  id?: string;
  control: Control<T>;
  name: FieldPath<T>;
  initialPreviewUrl?: string | null;
}

export function LogoUpload<T extends FieldValues>(props: LogoUploadProps<T>) {
  const { id, initialPreviewUrl } = props;
  const { field } = useController({ control: props.control, name: props.name });

  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    initialPreviewUrl ?? null,
  );

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result as string);
        setPreviewUrl(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const onCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropConfirm = useCallback(async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
    if (croppedBlob) {
      const file = new File([croppedBlob], "logo.png", { type: "image/png" });
      field.onChange(file);
      setPreviewUrl(URL.createObjectURL(croppedBlob));
      setImageSrc(null);
    }
  }, [imageSrc, croppedAreaPixels, field]);

  const handleCancel = useCallback(() => {
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  }, []);

  const handleRemove = useCallback(() => {
    field.onChange(null);
    setPreviewUrl(null);
    setImageSrc(null);
  }, [field]);

  if (imageSrc) {
    return (
      <div className={styles.cropper}>
        <div className={styles.cropperContainer}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>
        <RangeInput
          id="zoom-slider"
          label="Zoom"
          min={1}
          max={3}
          step={0.1}
          value={zoom}
          onChange={(e) => setZoom(Number(e.target.value))}
        />
        <div className={styles.actions}>
          <Button variant="ghost" onClick={handleCancel} icon={TbX}>
            Cancel
          </Button>
          <Button onClick={handleCropConfirm} icon={TbPhoto}>
            Crop & Use
          </Button>
        </div>
      </div>
    );
  }

  if (previewUrl) {
    return (
      <div className={styles.preview}>
        <img
          src={previewUrl}
          alt="Logo preview"
          className={styles.previewImg}
        />
        <div className={styles.previewActions}>
          <Button size="sm" variant="ghost" onClick={handleRemove} icon={TbX}>
            Remove
          </Button>
          <FileInputBtn
            size="sm"
            variant="ghost"
            icon={TbUpload}
            accept={[".png", ".jpg", ".jpeg", ".webp"]}
            onChange={(files) => onDrop(Array.from(files))}
          >
            Change
          </FileInputBtn>
        </div>
      </div>
    );
  }

  return (
    <Dropzone
      id={id}
      onFiles={onDrop}
      accept={imageAccept}
      hint="PNG, JPG or WebP"
    >
      <TbUpload size={32} />
      <p>Drag & drop a logo here, or click to select</p>
    </Dropzone>
  );
}
